import { Link, createFileRoute, redirect, useNavigate, useRouter } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";

import { ConfirmModal } from "@/components/ConfirmModal.tsx";
import { Trash2 } from "lucide-react";
import { UnauthorizedError, apiGet } from "@/lib/api.ts";
import { clearActiveOrgId, setActiveOrgId } from "@/lib/activeOrg.ts";
import {
  AuthApiError,
  type OrgMember,
  inviteMember,
  leaveOrg,
  listMembers,
  removeMember,
  updateMemberRole,
} from "@/lib/auth.ts";
import { relativeTime } from "@/lib/format.ts";

// FRE-279: org member management.
//
// Hits Better Auth's org plugin directly (`lib/auth.ts`). Admin gating reads
// the current user's role for the active org from `/api/v1/me` — the server
// is still the source of truth, this is purely for UI affordance.

type MeOrg = { id: string; slug: string; name: string; role: string };
type Me = {
  userId: string;
  organizationId: string;
  user: { id: string; name: string | null; email: string } | null;
  organizations: MeOrg[];
};

type Role = "owner" | "admin" | "member";

function isAdminRole(role: string): boolean {
  return role === "owner" || role === "admin";
}

export const Route = createFileRoute("/org/members")({
  beforeLoad: async () => {
    // Cheap auth gate identical to `/`: bounce to /login on 401 so the
    // members page doesn't render an empty shell behind a sign-in prompt.
    try {
      const me = await apiGet<Me>("/api/v1/me");
      return { me };
    } catch (err) {
      if (err instanceof UnauthorizedError) {
        throw redirect({ to: "/login", search: { next: "/org/members" } });
      }
      throw err;
    }
  },
  loader: async ({ context }) => context,
  component: MembersComponent,
});

function MembersComponent() {
  const { me } = Route.useLoaderData();

  // Resolve active org from /me — the worker already normalised this against
  // the X-Active-Org hint, so we trust `me.organizationId`.
  const activeOrg = me.organizations.find((o) => o.id === me.organizationId) ?? null;
  const myRole = activeOrg?.role ?? "member";
  const isAdmin = isAdminRole(myRole);

  const [members, setMembers] = useState<OrgMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!activeOrg) return;
    setLoading(true);
    setError(null);
    try {
      const res = await listMembers(activeOrg.id);
      setMembers(res.members);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load members");
    } finally {
      setLoading(false);
    }
  }, [activeOrg]);

  useEffect(() => {
    void load();
  }, [load]);

  if (!activeOrg) {
    return (
      <div className="max-w-3xl mx-auto">
        <h1 className="text-2xl font-semibold mb-4">Members</h1>
        <p className="text-sm text-muted-foreground">
          You don't have an active organization.{" "}
          <Link to="/orgs/new" className="text-primary hover:underline">
            Create one
          </Link>
          .
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold">Members</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            {activeOrg.name}
            <span className="mx-1.5 text-muted-foreground/40">·</span>
            <Link to="/org/invitations" className="text-sm text-primary hover:underline">
              Pending invitations →
            </Link>
          </p>
        </div>
      </div>

      {error ? (
        <div className="mb-4 rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive flex items-center justify-between gap-3">
          <span>{error}</span>
          <button
            type="button"
            onClick={() => void load()}
            className="rounded-md border border-destructive/40 px-2.5 py-1 text-xs font-medium text-destructive hover:bg-destructive/20"
          >
            Retry
          </button>
        </div>
      ) : null}

      {actionError ? (
        <div className="mb-4 rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive flex items-center justify-between gap-3">
          <span>{actionError}</span>
          <button
            type="button"
            onClick={() => setActionError(null)}
            className="rounded-md border border-destructive/40 px-2.5 py-1 text-xs font-medium text-destructive hover:bg-destructive/20"
          >
            Dismiss
          </button>
        </div>
      ) : null}

      {isAdmin ? (
        <InviteForm
          organizationId={activeOrg.id}
          onInvited={() => {
            // Newly created invitations live on `/org/invitations`; surface
            // a hint instead of refetching members (they aren't a member yet).
            setActionError(null);
          }}
          onError={setActionError}
        />
      ) : null}

      <div className="rounded-md border border-border bg-card overflow-hidden">
        <div className="grid grid-cols-[1fr_auto_auto_auto] items-center gap-4 px-4 py-2 border-b border-border bg-muted/40 text-xs font-medium text-muted-foreground uppercase tracking-wide">
          <div>Name / email</div>
          <div>Role</div>
          <div>Joined</div>
          <div aria-hidden="true" className="w-16" />
        </div>
        {loading ? (
          <div className="px-6 py-8 text-center text-sm text-muted-foreground">Loading…</div>
        ) : members.length === 0 ? (
          <div className="px-6 py-8 text-center text-sm text-muted-foreground">No members.</div>
        ) : (
          members.map((m) => (
            <MemberRow
              key={m.id}
              member={m}
              isAdmin={isAdmin}
              isSelf={m.userId === me.userId}
              organizationId={activeOrg.id}
              onChange={() => void load()}
              onError={setActionError}
            />
          ))
        )}
      </div>

      <div className="mt-10 pt-6 border-t border-border">
        <LeaveOrgButton orgId={activeOrg.id} orgName={activeOrg.name} onError={setActionError} />
        <p className="mt-2 text-xs text-muted-foreground">
          Leaving removes you from this organization. If you're the last admin the server will block
          the request.
        </p>
      </div>
    </div>
  );
}

function InviteForm({
  organizationId,
  onInvited,
  onError,
}: {
  organizationId: string;
  onInvited: () => void;
  onError: (msg: string) => void;
}) {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<Role>("member");
  const [pending, setPending] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!email.trim()) return;
    setPending(true);
    setSuccess(null);
    try {
      const inv = await inviteMember({ email: email.trim(), role, organizationId });
      const link = `${window.location.origin}/invitations/${inv.id}`;
      try {
        await navigator.clipboard.writeText(link);
        setSuccess(`Invitation sent. Link copied to clipboard.`);
      } catch {
        setSuccess(`Invitation sent. Link: ${link}`);
      }
      setEmail("");
      onInvited();
    } catch (err) {
      onError(err instanceof Error ? err.message : "Invite failed");
    } finally {
      setPending(false);
    }
  }

  return (
    <form
      onSubmit={(e) => void handleSubmit(e)}
      className="mb-6 rounded-md border border-border bg-card p-4"
    >
      <h2 className="text-sm font-semibold text-foreground mb-3">Invite member</h2>
      <div className="flex flex-wrap items-end gap-3">
        <label className="flex-1 min-w-[200px]">
          <span className="block text-xs font-medium text-muted-foreground mb-1">Email</span>
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full rounded-md border border-input px-3 py-1.5 text-sm"
            placeholder="alice@example.com"
            autoComplete="email"
          />
        </label>
        <label>
          <span className="block text-xs font-medium text-muted-foreground mb-1">Role</span>
          <select
            value={role}
            onChange={(e) => setRole(e.target.value as Role)}
            className="rounded-md border border-input px-3 py-1.5 text-sm bg-card"
          >
            <option value="member">member</option>
            <option value="admin">admin</option>
          </select>
        </label>
        <button
          type="submit"
          disabled={pending}
          className="rounded-md bg-primary px-4 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
        >
          {pending ? "Sending…" : "Send invite"}
        </button>
      </div>
      {success ? <p className="mt-2 text-xs text-green-700">{success}</p> : null}
    </form>
  );
}

function MemberRow({
  member,
  isAdmin,
  isSelf,
  organizationId,
  onChange,
  onError,
}: {
  member: OrgMember;
  isAdmin: boolean;
  isSelf: boolean;
  organizationId: string;
  onChange: () => void;
  onError: (msg: string) => void;
}) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const displayName = member.user.name ?? member.user.email;

  async function handleRoleChange(e: React.ChangeEvent<HTMLSelectElement>) {
    const newRole = e.target.value;
    if (newRole === member.role) return;
    setBusy(true);
    try {
      await updateMemberRole(member.id, newRole, organizationId);
      onChange();
    } catch (err) {
      const msg = err instanceof AuthApiError ? err.message : "Failed to update role";
      onError(msg);
    } finally {
      setBusy(false);
    }
  }

  async function handleRemove() {
    setBusy(true);
    try {
      await removeMember(member.id, organizationId);
      setConfirmOpen(false);
      onChange();
    } catch (err) {
      const msg = err instanceof AuthApiError ? err.message : "Failed to remove member";
      onError(msg);
      setConfirmOpen(false);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="grid grid-cols-[1fr_auto_auto_auto] items-center gap-4 px-4 py-3 border-b border-border last:border-b-0">
      <div className="min-w-0">
        <div className="text-sm font-medium text-foreground truncate">
          {displayName}
          {isSelf ? <span className="ml-1.5 text-xs text-muted-foreground">(you)</span> : null}
        </div>
        {member.user.name ? (
          <div className="text-xs text-muted-foreground truncate">{member.user.email}</div>
        ) : null}
      </div>
      <div>
        {isAdmin && !isSelf ? (
          <select
            value={member.role}
            onChange={(e) => void handleRoleChange(e)}
            disabled={busy}
            aria-label={`Role for ${displayName}`}
            className="rounded-md border border-input px-2 py-1 text-xs bg-card disabled:opacity-50"
          >
            <option value="member">member</option>
            <option value="admin">admin</option>
            <option value="owner">owner</option>
          </select>
        ) : (
          <span className="text-xs font-medium text-foreground/80 uppercase tracking-wide">
            {member.role}
          </span>
        )}
      </div>
      <div className="text-xs text-muted-foreground whitespace-nowrap">
        {relativeTime(member.createdAt)}
      </div>
      <div className="w-16 text-right">
        {isAdmin && !isSelf ? (
          <button
            type="button"
            onClick={() => setConfirmOpen(true)}
            disabled={busy}
            aria-label={`Remove ${displayName}`}
            className="inline-flex items-center gap-1 rounded-md border border-input px-2 py-1 text-xs font-medium text-destructive hover:bg-destructive/10 disabled:opacity-50"
          >
            <Trash2 className="size-4" />
            Remove
          </button>
        ) : null}
      </div>

      <ConfirmModal
        open={confirmOpen}
        title={`Remove ${displayName}?`}
        body={
          <p>
            They will lose access to this organization immediately. They can be re-invited later.
          </p>
        }
        confirmLabel="Remove member"
        danger
        busy={busy}
        onConfirm={() => void handleRemove()}
        onCancel={() => setConfirmOpen(false)}
      />
    </div>
  );
}

function LeaveOrgButton({
  orgId,
  orgName,
  onError,
}: {
  orgId: string;
  orgName: string;
  onError: (msg: string) => void;
}) {
  const navigate = useNavigate();
  const router = useRouter();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  async function handleLeave() {
    setBusy(true);
    try {
      await leaveOrg(orgId);
      // Active-org pointer is now stale; clear it and refetch /me so the
      // next loader run picks up the first remaining org (or routes to
      // /orgs/new if there are none).
      clearActiveOrgId();
      try {
        const me = await apiGet<Me>("/api/v1/me");
        if (me.organizations.length > 0) {
          await setActiveOrgId(me.organizations[0].id);
          await router.invalidate();
          await navigate({ to: "/" });
        } else {
          await router.invalidate();
          await navigate({ to: "/orgs/new" });
        }
      } catch {
        // /me failed (e.g. transient) — still navigate home and let the root
        // loader retry. clearActiveOrgId already happened so we won't loop on
        // a now-forbidden org id.
        await router.invalidate();
        await navigate({ to: "/" });
      }
    } catch (err) {
      const msg = err instanceof AuthApiError ? err.message : "Failed to leave org";
      onError(msg);
      setConfirmOpen(false);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setConfirmOpen(true)}
        className="text-sm font-medium text-destructive hover:underline"
      >
        Leave organization
      </button>
      <ConfirmModal
        open={confirmOpen}
        title={`Leave ${orgName}?`}
        body={
          <p>
            You'll lose access to this organization's files. If you're the last admin, this will
            fail and you'll need to promote someone else first.
          </p>
        }
        confirmLabel="Leave organization"
        danger
        busy={busy}
        onConfirm={() => void handleLeave()}
        onCancel={() => setConfirmOpen(false)}
      />
    </>
  );
}
